interface SetCookieOptions {
  name: string;
  value: string;
  domain?: string;
  path?: string;
  maxAge?: number;
  expires?: Date;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

interface RemoveCookieOptions {
  domain?: string;
  path?: string;
}

/**
 * Gets the current domain from window.location, removing 'www.' prefix if present
 */
const getCurrentDomain = (): string => {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }

  const hostname = window.location.hostname;
  // Remove 'www.' prefix and return the domain
  return hostname.replace(/^www\./, '');
};

/**
 * Sets a cookie with the specified options
 * @param options Cookie configuration options
 */
export const setCookie = (options: SetCookieOptions | { name: string; value: string }) => {
  // Handle both old and new API for backward compatibility
  const {
    name,
    value,
    domain = getCurrentDomain(),
    path = '/',
    maxAge = undefined,
    expires = undefined,
    secure = window.location?.protocol === 'https:',
    sameSite = 'Lax',
  } = 'domain' in options ? options : { ...options, domain: undefined };

  if (!name || typeof name !== 'string') {
    console.warn('setCookie: Cookie name must be a non-empty string');
    return;
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  } else if (expires) {
    cookieString += `; expires=${expires.toUTCString()}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }

  try {
    document.cookie = cookieString;
  } catch (error) {
    console.error('setCookie: Failed to set cookie', error);
  }
};

/**
 * Removes a cookie by setting its expiration to the past
 * @param name Cookie name to remove
 * @param options Optional domain and path to match the cookie that was set
 */
export const removeCookie = (name: string, options: RemoveCookieOptions = {}) => {
  if (!name || typeof name !== 'string') {
    console.warn('removeCookie: Cookie name must be a non-empty string');
    return;
  }

  const { domain = getCurrentDomain(), path = '/' } = options;

  // Set multiple variations to ensure removal across different domain/path combinations
  const removeVariations = [
    `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`,
    `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}; path=${path}`,
    `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${domain}; path=${path}`,
  ];

  try {
    removeVariations.forEach((cookieString) => {
      document.cookie = cookieString;
    });
  } catch (error) {
    console.error('removeCookie: Failed to remove cookie', error);
  }
};

/**
 * Gets the value of a cookie by name
 * @param name Cookie name to retrieve
 * @returns Cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
  if (!name || typeof name !== 'string') {
    console.warn('getCookie: Cookie name must be a non-empty string');
    return undefined;
  }

  if (typeof document === 'undefined') {
    return undefined;
  }

  const encodedName = encodeURIComponent(name);
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith(`${encodedName}=`)) {
      const value = trimmedCookie.substring(encodedName.length + 1);
      try {
        return decodeURIComponent(value);
      } catch (error) {
        // If decoding fails, return the raw value
        console.warn('getCookie: Failed to decode cookie value', error);
        return value;
      }
    }
  }

  return undefined;
};
